import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Ellipsis } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog } from '../ui';
import { ADMIN_NAV, isActivePath, NavItem, PRIMARY_NAV, visibleTo } from './navigation';

/** iOS-style tab bar for small screens; everything that doesn't fit lives under More. */
export const MobileTabBar: React.FC = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const primary = visibleTo(PRIMARY_NAV, user?.role);
  const tabs = primary.filter((item) => item.tabLabel);
  // Search has its own button in the mobile header
  const morePrimary = primary.filter((item) => !item.tabLabel && item.href !== '/search');
  const moreAdmin = visibleTo(ADMIN_NAV, user?.role);
  const moreActive = [...morePrimary, ...moreAdmin].some((item) => isActivePath(pathname, item.href));

  useEffect(() => setMoreOpen(false), [pathname]);

  const go = (href: string) => {
    setMoreOpen(false);
    navigate(href);
  };

  return (
    <>
      <nav
        aria-label="Tabs"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200/70 bg-white/85 font-ui backdrop-blur-xl backdrop-saturate-150 lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <ul className="mx-auto grid h-14 max-w-lg" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
          {tabs.map((item) => (
            <li key={item.href}>
              <NavLink to={item.href} className={tabClass}>
                {({ isActive }) => <TabContent item={item} active={isActive} />}
              </NavLink>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-haspopup="dialog"
              className={tabClass({ isActive: moreActive || moreOpen })}
            >
              <Ellipsis className="h-6 w-6" strokeWidth={moreActive ? 2 : 1.75} />
              <span>More</span>
            </button>
          </li>
        </ul>
      </nav>

      <Dialog open={moreOpen} onClose={() => setMoreOpen(false)} label="More" handle surface="muted" className="max-h-[88dvh] sm:max-w-sm">
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-1 sm:pt-5"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <MoreGroup items={morePrimary} pathname={pathname} onGo={go} />
          {moreAdmin.length > 0 && (
            <>
              <p className="px-4 pb-1.5 pt-5 text-[13px] text-neutral-500">Admin</p>
              <MoreGroup items={moreAdmin} pathname={pathname} onGo={go} />
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};

const tabClass = ({ isActive }: { isActive: boolean }) =>
  `flex h-full w-full select-none flex-col items-center justify-center gap-0.5 text-[10.5px] font-medium transition-[color,transform] duration-150 [-webkit-tap-highlight-color:transparent] active:scale-[0.92] focus-visible:outline-none ${
    isActive ? 'text-primary-600' : 'text-neutral-400'
  }`;

const TabContent: React.FC<{ item: NavItem; active: boolean }> = ({ item, active }) => {
  const Icon = item.icon;
  return (
    <>
      <Icon className="h-6 w-6" strokeWidth={active ? 2 : 1.75} aria-hidden="true" />
      <span>{item.tabLabel}</span>
    </>
  );
};

const MoreGroup: React.FC<{ items: NavItem[]; pathname: string; onGo: (href: string) => void }> = ({
  items,
  pathname,
  onGo,
}) => {
  if (!items.length) return null;
  return (
    <ul className="overflow-hidden rounded-2xl bg-white">
      {items.map((item, index) => {
        const Icon = item.icon;
        const active = isActivePath(pathname, item.href);
        return (
          <li key={item.href}>
            <button
              type="button"
              onClick={() => onGo(item.href)}
              aria-current={active ? 'page' : undefined}
              className="flex h-12 w-full items-center gap-3 pl-3 text-left transition-colors [-webkit-tap-highlight-color:transparent] active:bg-neutral-100"
            >
              <span
                className={`inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] ${
                  active ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-ink'
                }`}
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" />
              </span>
              <span
                className={`flex h-full flex-1 items-center justify-between pr-3 text-[16px] text-ink ${
                  index > 0 ? 'border-t border-neutral-200/70' : ''
                }`}
              >
                {item.name}
                <ChevronRight className="h-4 w-4 text-neutral-300" strokeWidth={2} />
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};
